import React, { useState, useEffect, useMemo, useRef } from 'react';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  IconButton,
  Tooltip,
  useDisclosure,
  Text,
  useColorModeValue,
  Select as ChakraSelect,
  FormControl,
  Spinner,
  Center,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Container,
  Badge,
  Icon,
  Divider,
  Alert,
  AlertIcon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiSettings,
  FiDownload,
  FiUpload,
  FiShield,
  FiMoreVertical,
  FiAlertCircle,
  FiSearch,
  FiX,
  FiMessageSquare,
  FiInfo,
} from 'react-icons/fi';
import { useHistory, useParams, useLocation, Redirect } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { CRM_VIEW_KEYS, readViewSearch, writeViewSearch } from '../../utils/crmViewSession';
import { useCrmDefaultOwnerScope, CRM_OWNER_SCOPE } from '../../hooks/useCrmDefaultOwnerScope';
import { useContactsTable } from '../../hooks/useContactsTable';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { applyVisibilityScope } from '../../utils/recordVisibility';
import { useTasks } from '../../hooks/useTasks';
import { useSmartLists } from '../../hooks/useSmartLists';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useDashboardAgentTools } from './agent/useDashboardAgentTools';
import { useCRMTablePreferences } from '../../hooks/useCRMTablePreferences';
import usePostcardAccess from '../../hooks/usePostcardAccess';
import CustomizableContactsTable from '../../components/CRM/CustomizableContactsTable';
import ContactDetailDrawer from '../../components/CRM/ContactDetailDrawer';
import QuickAddContact from '../../components/CRM/QuickAddContact';
import MyTasksWidget from '../../components/CRM/DashboardWidgets/MyTasksWidget';
import LoanAnniversariesWidget from '../../components/CRM/DashboardWidgets/LoanAnniversariesWidget';
import DailyBriefingCard from '../../components/CRM/DashboardWidgets/DailyBriefingCard';
import { canAccessNewFeatures } from '../../utils/featureAccess';
import SmartListDropdown from '../../components/CRM/SmartLists/SmartListDropdown';
import SmartListBuilderModal from '../../components/CRM/SmartLists/SmartListBuilderModal';
import ContactsFilterToolbar from '../../components/CRM/ContactsFilterToolbar';
import TableCustomizationModal from '../../components/CRM/TableCustomizationModal';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import UnifiedEventModal from '../../components/CRM/Calendar/UnifiedEventModal';
import TaskCompletionActivityPrompt, { taskHasLoggableEntity } from '../../components/CRM/TaskCompletionActivityPrompt';
import ImportModal from '../../components/CRM/Import/ImportModal';
import BulkContactActionsBar from '../../components/CRM/BulkContactActionsBar';
import { CRMAddIdeaModal } from './Ideas';
import {
  BulkContactTagModal,
  BulkContactTypeModal,
  BulkContactOwnershipModal,
  BulkContactRecordScopeModal,
  BulkDeleteContactsModal,
  BulkExportContactsModal,
  BulkEmailModal,
  BulkSMSModal,
} from '../../components/CRM/BulkContactModals';
import BulkPostcardModal from '../../components/CRM/BulkPostcardModal';
import BulkEnrollCampaignModal from './Workspace/BulkEnrollCampaignModal';
import {
  DEFAULT_CONTACTS_PREFERENCES,
  ALL_CONTACTS_COLUMNS,
} from '../../components/CRM/contactsTableColumnDefinitions';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import { supabase, fetchAllRows, transformContactToCamelCase } from '../../utils/supabase';
import moment from 'moment';

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  return {
    userId: params.get('userId'),
    search: params.get('search'),
    sort: params.get('sort'),
    sortDir: params.get('sortDir'),
    page: params.get('page'),
    pageSize: params.get('pageSize'),
    list: params.get('list'),
    filters: params.get('filters'),
  };
}

function DashboardPageContent({ defaultOwnerScope }) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { contactId: urlContactId } = useParams();
  const { canSendPostcards } = usePostcardAccess();

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Parse URL params once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialParams = useMemo(() => parseUrlParams(location.search), []);

  // Refs for deferred reconciliation (smart list & filters load async)
  const urlSmartListRef = useRef(initialParams.list);
  const urlFiltersRef = useRef(initialParams.filters);

  const [selectedContact, setSelectedContact] = useState(null);
  // Admins open on the scope they chose in CRM Settings > Default View.
  // An explicit ?userId= param always wins ('all' is the All Users sentinel).
  // Non-admins are scoped to self downstream (ownerId uses currentUser.uid),
  // so '' is correct for them.
  const [selectedUserId, setSelectedUserId] = useState(() => {
    if (initialParams.userId) {
      return initialParams.userId === 'all' ? '' : initialParams.userId;
    }
    if (!isCRMAdmin) return '';
    return defaultOwnerScope === CRM_OWNER_SCOPE.ALL ? '' : (currentUser?.uid || '');
  });
  const [crmUsers, setCrmUsers] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [isAllContactsSelected, setIsAllContactsSelected] = useState(false);
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completedTaskForPrompt, setCompletedTaskForPrompt] = useState(null);
  const [tableSearchTerm, setTableSearchTerm] = useState(initialParams.search || '');
  const [activeSearchTerm, setActiveSearchTerm] = useState(initialParams.search || '');

  // Search handler functions - now triggers server-side search
  const handleSearch = () => {
    setActiveSearchTerm(tableSearchTerm);
    setGlobalSearch(tableSearchTerm);
  };

  const handleClearSearch = () => {
    setTableSearchTerm('');
    setActiveSearchTerm('');
    setGlobalSearch('');
  };
  
  // Use persistent table preferences hook - pass contacts-specific defaults
  const { preferences: tablePreferences, updatePreferences, resetToDefaults: resetTablePrefs, loading: prefsLoading } = useCRMTablePreferences(currentUser?.uid, {
    defaultPreferences: DEFAULT_CONTACTS_PREFERENCES,
    preferencesKey: 'crmContactsTablePreferences', // Separate from deals preferences
  });

  // --- Disclosures ---
  const { isOpen: isContactDetailOpen, onOpen: onContactDetailOpen, onClose: onContactDetailClose } = useDisclosure();
  const { isOpen: isAddContactOpen, onOpen: onAddContactOpen, onClose: onAddContactClose } = useDisclosure();
  const { isOpen: isSmartListBuilderOpen, onOpen: onSmartListBuilderOpen, onClose: onSmartListBuilderClose } = useDisclosure();
  const { isOpen: isTaskDetailOpen, onOpen: onTaskDetailOpen, onClose: onTaskDetailClose } = useDisclosure();
  const { isOpen: isTaskModalOpen, onOpen: onTaskModalOpen, onClose: onTaskModalClose } = useDisclosure();
  const { isOpen: isActivityPromptOpen, onOpen: onActivityPromptOpen, onClose: onActivityPromptClose } = useDisclosure();
  const { isOpen: isTableCustomOpen, onOpen: onTableCustomOpen, onClose: onTableCustomClose } = useDisclosure();
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
  const { isOpen: isBulkTagOpen, onOpen: onBulkTagOpen, onClose: onBulkTagClose } = useDisclosure();
  const { isOpen: isBulkTypeOpen, onOpen: onBulkTypeOpen, onClose: onBulkTypeClose } = useDisclosure();
  const { isOpen: isBulkOwnershipOpen, onOpen: onBulkOwnershipOpen, onClose: onBulkOwnershipClose } = useDisclosure();
  const { isOpen: isBulkScopeOpen, onOpen: onBulkScopeOpen, onClose: onBulkScopeClose } = useDisclosure();
  const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
  const { isOpen: isBulkExportOpen, onOpen: onBulkExportOpen, onClose: onBulkExportClose } = useDisclosure();
  const { isOpen: isBulkEmailOpen, onOpen: onBulkEmailOpen, onClose: onBulkEmailClose } = useDisclosure();
  const { isOpen: isBulkSMSOpen, onOpen: onBulkSMSOpen, onClose: onBulkSMSClose } = useDisclosure();
  const { isOpen: isBulkPostcardOpen, onOpen: onBulkPostcardOpen, onClose: onBulkPostcardClose } = useDisclosure();
  const { isOpen: isBulkEnrollOpen, onOpen: onBulkEnrollOpen, onClose: onBulkEnrollClose } = useDisclosure();
  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackClose } = useDisclosure();

  const [dialpadEnabled, setDialpadEnabled] = useState(false);

  const effectiveUserId = isCRMAdmin ? (selectedUserId || currentUser?.uid) : currentUser?.uid;

  // True when the active filter is the admin's own data (no selection, or self selected).
  // Used so the "CRM Dashboard" framing is preserved when defaulted to self.
  const isViewingSelf = !selectedUserId || selectedUserId === currentUser?.uid;

  // --- Style Values ---
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const widgetBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Additional color values for inline stats cards
  const activeDealsIconBg = useColorModeValue('green.50', 'green.900');

  // --- State & Hooks ---
  const [userStats, setUserStats] = useState(null);

  // Load CRM users — needed by all viewers (not just admins) so owner names
  // resolve on records shared via teams/book shares (multi-owner Phase 2).
  useEffect(() => {
    const loadCrmUsers = async () => {
      const usersQuery = query(collection(db, 'users'), where('CRMUser', '==', true));
      const snapshot = await getDocs(usersQuery);
      const users = snapshot.docs.map(doc => ({
        uid: doc.id,
        name: doc.data().Name || doc.data().name || doc.data().email,
        ...doc.data(),
      }));
      setCrmUsers(sortCRMUsersWithDTCPriority(users));
    };
    loadCrmUsers();
  }, []);

  // Check if current user has Dialpad enabled (gates Bulk SMS button)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setDialpadEnabled(userData.dialpadCapable === true && userData.dialpadEnabled === true);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Create users lookup map for Owner column
  const usersMap = useMemo(() => {
    const map = new Map();
    crmUsers.forEach(user => {
      map.set(user.uid, user.name);
    });
    return map;
  }, [crmUsers]);

  // Multi-owner visibility scopes (owned + team book-shares + record shares,
  // + company records for admins). Admin with no user selected = All Users.
  // Contacts scope drives the table; deals scope drives the stat cards.
  const { scope: contactsOwnerScope } = useVisibilityScope({
    entityType: 'contact',
    targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
  });
  const { scope: dealsStatsScope } = useVisibilityScope({
    entityType: 'deal',
    targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
  });

  // Server-side contacts table hook
  // For CRM Admins: null = all users, selectedUserId = specific user
  // For regular users: always filter to their own contacts
  const {
    contacts: defaultContacts,
    totalCount: contactsTotalCount,
    totalPages: contactsTotalPages,
    currentPage: contactsCurrentPage,
    pageSize: contactsPageSize,
    loading: contactsLoading,
    filters: activeFilters,
    sort: contactsSort,
    searchTerm: contactsSearchTerm,
    applyFilters,
    applySort,
    setPage: setContactsPage,
    setPageSize: setContactsPageSize,
    setGlobalSearch,
    refresh: refreshContacts,
    fetchAllIds: fetchAllContactIds,
    fetchContactsByIds,
  } = useContactsTable({
    ownerScope: contactsOwnerScope,
    pageSize: parseInt(initialParams.pageSize, 10) || 100,
    initialSort: {
      field: initialParams.sort || tablePreferences.sortBy || 'createdAt',
      direction: initialParams.sortDir || tablePreferences.sortDirection || 'desc',
    },
    initialPage: parseInt(initialParams.page, 10) || 1,
    globalSearchTerm: initialParams.search || activeSearchTerm,
  });

  // Tasks hook — active statuses only. 'in-progress' is a real status
  // (TaskDetailModal, DealPage), so filtering to 'pending' alone made started
  // tasks vanish from the dashboard widget.
  const tasksUserId = effectiveUserId;
  const { tasks, updateTask, createTask, deleteTask, completeTask, reopenTask } = useTasks(tasksUserId, { type: 'crm', status: ['pending', 'in-progress'] });

  // Smart Lists hook (Supabase-based)
  const {
    smartLists,
    personalLists,
    globalLists,
    selectedListId: selectedNewListId,
    setSelectedListId: setSelectedNewListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    duplicateSmartList,
    setAsDefault: setSmartListAsDefault,
    loading: smartListsLoading,
  } = useSmartLists({ userId: currentUser?.uid, entityType: 'contacts' });

  // Reconcile URL smart list / filters once async data finishes loading
  const hasReconciledUrlRef = useRef(false);
  useEffect(() => {
    if (smartListsLoading || hasReconciledUrlRef.current) return;
    hasReconciledUrlRef.current = true;

    if (urlSmartListRef.current) {
      const listId = urlSmartListRef.current;
      const list = smartLists.find(l => l.id === listId);
      if (list) {
        setSelectedNewListId(listId);
        applyFilters(list.filters || []);
      }
    } else if (urlFiltersRef.current) {
      try {
        const parsed = JSON.parse(decodeURIComponent(urlFiltersRef.current));
        if (Array.isArray(parsed)) {
          applyFilters(parsed);
        }
      } catch (e) {
        console.warn('Failed to parse URL filters:', e);
      }
    }

    // Both refs are cleared regardless of which branch ran: they gate the URL
    // sync effect below, and a hand-written URL carrying both params would
    // otherwise leave one set and block syncing forever.
    urlSmartListRef.current = null;
    urlFiltersRef.current = null;
  }, [smartListsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // FutureBot: register this page's hands-on agent tools (filters, search,
  // owner scope, smartlists). Must stay above any early return (hooks rule).
  const { registration: agentRegistration } = useDashboardAgentTools({
    currentUser,
    isCRMAdmin,
    crmUsers,
    filters: activeFilters,
    applyFilters,
    sort: contactsSort,
    applySort,
    totalCount: contactsTotalCount,
    currentPage: contactsCurrentPage,
    pageSize: contactsPageSize,
    loading: contactsLoading,
    activeSearchTerm,
    setTableSearchTerm,
    setActiveSearchTerm,
    setGlobalSearch,
    selectedUserId,
    setSelectedUserId,
    smartLists,
    selectedListId: selectedNewListId,
    setSelectedListId: setSelectedNewListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    setAsDefault: setSmartListAsDefault,
  });
  useAgentPage(agentRegistration);

  // FeatureTour: named actions the dashboard tours can invoke (tourRegistry.js).
  // selectFirstContact seeds a one-row selection so the bulk-actions tour can
  // spotlight the bulk bar (it only renders while something is selected).
  const tourActions = useMemo(() => ({
    openAddContact: onAddContactOpen,
    openImportForTour: onImportOpen,
    selectFirstContact: () => {
      const first = defaultContacts[0];
      if (first) {
        setIsAllContactsSelected(false);
        setSelectedContactIds([first.id]);
      }
    },
  }), [onAddContactOpen, onImportOpen, defaultContacts]);
  useTourActions(tourActions);

  // Contacts are now always from the server-side filtered hook
  const contacts = defaultContacts;

  // Fetch deal stats from Supabase
  // Uses same owner logic as contacts table: null = all users for admins
  // Uses pagination to bypass Supabase's 1000 row limit
  useEffect(() => {
    if (!currentUser || !supabase) return;
    
    const fetchDealStats = async () => {
      try {
        // Use fetchAllRows to get ALL active deals (bypasses 1000 row limit)
        const deals = await fetchAllRows('crm_deals', 'id, stage, amount, estimated_closing_date', (query) => {
          // Filter to active deals only (exclude won and lost)
          // Active = excludes terminal-positive (funded variants) and terminal-negative (lost),
          // plus legacy deal-won during the Phase A transition window.
          query = query.not('stage', 'in', '("deal-won","deal-lost","loan-funded","loan-brokercheck-received","loan-finalized")');
          // Multi-owner visibility (no-op for admin All Users)
          query = applyVisibilityScope(query, dealsStatsScope);
          return query;
        });
        
        // Calculate stats from the deals data
        const totalDeals = deals?.length || 0;
        const newLeads = deals?.filter(d => d.stage === 'lead-new').length || 0;
        const pipelineValue = deals?.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) || 0;
        
        // Count deals closing this month
        const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
        const closingThisMonth = deals?.filter(d => {
          if (!d.estimated_closing_date) return false;
          return d.estimated_closing_date >= startOfMonth && d.estimated_closing_date <= endOfMonth;
        }).length || 0;
        
        setUserStats({
          deals: {
            totalDeals,
            stage_lead_new: newLeads,
            pipelineValue,
            closingThisMonth,
          },
        });
      } catch (err) {
        console.error('Error calculating deal stats:', err);
        setUserStats({
          deals: { totalDeals: 0, stage_lead_new: 0, pipelineValue: 0, closingThisMonth: 0 },
        });
      }
    };
    
    fetchDealStats();
    
    // Realtime stats refresh — one filtered channel per accessible owner
    // (scoped), unfiltered for All Users. Capped to avoid channel sprawl.
    const scopedOwnerIds =
      dealsStatsScope && dealsStatsScope.mode === 'scoped'
        ? (dealsStatsScope.ownerIds || [dealsStatsScope.targetUid]).slice(0, 10)
        : null;
    
    const statsSuffix = Math.random().toString(36).slice(2, 8);
    const statsChannels = [];
    if (scopedOwnerIds === null) {
      statsChannels.push(
        supabase
          .channel(`deals-stats-changes-all-${statsSuffix}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_deals' }, fetchDealStats)
          .subscribe()
      );
    } else {
      scopedOwnerIds.forEach((watchedOwnerId) => {
        statsChannels.push(
          supabase
            .channel(`deals-stats-changes-${watchedOwnerId}-${statsSuffix}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'crm_deals', filter: `owner_id=eq.${watchedOwnerId}` },
              fetchDealStats
            )
            .subscribe()
        );
      });
    }
    
    return () => {
      statsChannels.forEach((ch) => ch.unsubscribe());
    };
  }, [currentUser, dealsStatsScope]);


  // Calculate stats (SCALABILITY FIX - uses aggregated data)
  const stats = useMemo(() => {
    const tasksDueToday = tasks.filter(t => t.dueDate && moment(t.dueDate.toDate ? t.dueDate.toDate() : t.dueDate).isSame(moment(), 'day')).length;
    const tasksDueThisWeek = tasks.filter(t => t.dueDate && moment(t.dueDate.toDate ? t.dueDate.toDate() : t.dueDate).isSame(moment(), 'week')).length;
    const tasksPastDue = tasks.filter(t => t.dueDate && moment(t.dueDate.toDate ? t.dueDate.toDate() : t.dueDate).isBefore(moment(), 'day')).length;
    const totalTasks = tasks.length;
    
    // Use aggregated stats from userStats document
    const dealStats = userStats?.deals || {};
    const activeDeals = dealStats.totalDeals || 0;
    const newLeads = dealStats.stage_lead_new || 0;
    const pipelineValue = dealStats.pipelineValue || 0;
    const closingThisMonth = dealStats.closingThisMonth || 0;
    
    return {
      activeDeals,
      tasksDueToday,
      tasksDueThisWeek,
      tasksPastDue,
      totalTasks,
      newLeads,
      pipelineValue,
      closingThisMonth,
    };
  }, [tasks, userStats]);


  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        onAddContactOpen();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onAddContactOpen]);

  // URL Parameter Handling — open the contact from /crm/dashboard/:contactId.
  // Goes through handleContactClick so a contact outside the current table page
  // is fetched directly instead of silently never opening. The ref ensures one
  // open per contact id (it resets when the drawer closes and clears the URL).
  const handledUrlContactIdRef = useRef(null);
  useEffect(() => {
    if (!urlContactId) {
      handledUrlContactIdRef.current = null;
      return;
    }
    if (contactsLoading) return;
    if (handledUrlContactIdRef.current === urlContactId) return;
    handledUrlContactIdRef.current = urlContactId;
    handleContactClick(urlContactId);
  }, [urlContactId, contactsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear "all selected" state when filters or search changes
  useEffect(() => {
    if (isAllContactsSelected) {
      setSelectedContactIds([]);
      setIsAllContactsSelected(false);
    }
  }, [activeFilters, activeSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync committed filter state to URL via history.replace()
  // Back button navigates to the previous PAGE, not the previous filter state.
  useEffect(() => {
    // The smart list and filters from the URL are applied asynchronously, once
    // useSmartLists resolves. Writing before then would strip them from both the
    // URL and the saved snapshot.
    if (urlSmartListRef.current || urlFiltersRef.current) return;

    const params = new URLSearchParams();

    // Admins: always record the owner choice explicitly. Omitting it for
    // "All Users" makes that selection unrecoverable, because the Default View
    // preference fills the gap on the next mount.
    if (isCRMAdmin) {
      params.set('userId', selectedUserId || 'all');
    }

    if (activeSearchTerm) {
      params.set('search', activeSearchTerm);
    }

    const currentSortField = contactsSort?.field || tablePreferences.sortBy || 'createdAt';
    const currentSortDir = contactsSort?.direction || tablePreferences.sortDirection || 'desc';
    if (currentSortField !== 'createdAt') {
      params.set('sort', currentSortField);
    }
    if (currentSortDir !== 'desc') {
      params.set('sortDir', currentSortDir);
    }

    if (contactsCurrentPage > 1) {
      params.set('page', String(contactsCurrentPage));
    }

    if (contactsPageSize !== 100) {
      params.set('pageSize', String(contactsPageSize));
    }

    if (selectedNewListId) {
      params.set('list', selectedNewListId);
    }

    if (!selectedNewListId && activeFilters && activeFilters.length > 0) {
      params.set('filters', encodeURIComponent(JSON.stringify(activeFilters)));
    }

    const search = params.toString();
    const newSearch = search ? `?${search}` : '';

    // Remember the view for this tab so returning via the sidebar (which always
    // navigates to a bare /crm/dashboard) can replay it.
    writeViewSearch(CRM_VIEW_KEYS.dashboard, newSearch);

    if (location.search !== newSearch) {
      history.replace({ pathname: location.pathname, search: newSearch });
    }
    // smartListsLoading is a dependency so this re-runs once the reconciliation
    // effect above (declared first, so it runs first) has cleared the refs -
    // including when the saved list no longer exists and no state changes.
  }, [selectedUserId, isCRMAdmin, activeSearchTerm, contactsSort, contactsCurrentPage, contactsPageSize, selectedNewListId, activeFilters, tablePreferences.sortBy, tablePreferences.sortDirection, smartListsLoading, history, location.search, location.pathname]);

  // --- Handlers ---
  const handleContactClick = async (contactOrId) => {
    // Handle both contact objects and contactId strings
    if (!contactOrId) {
      toast({
        title: 'Contact not found',
        description: 'Could not load contact details',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (typeof contactOrId === 'string') {
      // It's a contactId. The table only holds the current page, so a contact
      // clicked from a widget (e.g. Anniversaries & Birthdays) is usually not
      // in it — fall back to fetching the record directly.
      const contact = contacts.find(c => c.id === contactOrId);
      if (contact) {
        setSelectedContact(contact);
        onContactDetailOpen();
        return;
      }
      try {
        const { data, error } = await supabase
          .from('crm_contacts')
          .select('*')
          .eq('id', contactOrId)
          .maybeSingle();
        if (error) throw error;
        if (!data) throw new Error('Contact does not exist');
        setSelectedContact(transformContactToCamelCase(data));
        onContactDetailOpen();
      } catch (err) {
        console.error('Error loading contact:', err);
        toast({
          title: 'Contact not found',
          description: 'Could not load contact details',
          status: 'warning',
          duration: 3000,
        });
      }
    } else {
      // It's a full contact object
      setSelectedContact(contactOrId);
      onContactDetailOpen();
    }
  };
  
  const handleDealClick = (dealId) => {
    history.push(`/crm/deals/${dealId}`);
  };

  const handleContactCreated = (newContact) => {
    setSelectedContact(newContact);
    onContactDetailOpen();
    refreshContacts();
  };
  const handleSelectionChange = (contactId) => {
    // When user manually changes selection, exit "all selected" mode
    if (isAllContactsSelected) {
      setIsAllContactsSelected(false);
    }
    setSelectedContactIds(prev =>
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };
  const handleSelectAll = () => {
    if (selectedContactIds.length === contacts.length) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(contacts.map(c => c.id));
    }
  };
  
  // Handler for "Select All X contacts" banner action
  const handleSelectAllMatchingFilters = async () => {
    setIsSelectAllLoading(true);
    try {
      const allIds = await fetchAllContactIds();
      setSelectedContactIds(allIds);
      setIsAllContactsSelected(true);
      toast({
        title: `Selected ${allIds.length.toLocaleString()} contacts`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error selecting all contacts:', error);
      toast({
        title: 'Error selecting contacts',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSelectAllLoading(false);
    }
  };
  
  // Handler for clearing all selections
  const handleClearAllSelection = () => {
    setSelectedContactIds([]);
    setIsAllContactsSelected(false);
  };
  
  const handleBulkSuccess = () => {
    setSelectedContactIds([]);
    setIsAllContactsSelected(false);
    refreshContacts();
  };
  // After a linked task is completed, offer to log the interaction as an activity
  const promptActivityLogIfLinked = (task) => {
    if (taskHasLoggableEntity(task)) {
      setCompletedTaskForPrompt(task);
      onActivityPromptOpen();
    }
  };
  const handleTaskComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      if (task.status === 'completed') {
        await reopenTask(taskId);
        toast({ title: 'Task reopened', status: 'success', duration: 2000 });
      } else {
        const result = await completeTask(taskId);
        toast({ title: 'Task completed', status: 'success', duration: 2000 });
        if (result?.success) promptActivityLogIfLinked(task);
      }
    } catch (error) {
      toast({ title: 'Error updating task', description: error.message, status: 'error', duration: 3000 });
    }
  };
  // TaskDetailModal shows its own completion toast, so this wrapper only completes + prompts
  const handleTaskCompleteFromDetail = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const result = await completeTask(taskId);
    if (result?.success && task) promptActivityLogIfLinked(task);
    return result;
  };
  const handleActivityPromptClose = () => {
    onActivityPromptClose();
    setCompletedTaskForPrompt(null);
  };
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    onTaskDetailOpen();
  };
  const handleSaveTask = (taskId, taskData) => taskId ? updateTask(taskId, taskData) : createTask(taskData);
  const handleDeleteTask = (taskId) => deleteTask(taskId);
  const handleEditTask = (task) => {
    setSelectedTask(task);
    onTaskDetailClose();
    onTaskModalOpen();
  };
  const handleTaskEditModalClose = () => {
    onTaskModalClose();
    // Stale selection would make the next open edit the wrong task
    setSelectedTask(null);
  };
  const handleSortChange = (columnId, direction) => {
    const column = ALL_CONTACTS_COLUMNS.find(c => c.id === columnId);
    const sortField = column?.sortField || columnId;
    updatePreferences({ sortBy: sortField, sortDirection: direction });
  };
  const handleFilterChange = (columnId, filterValue) => {
    updatePreferences({ filters: { ...tablePreferences.filters, [columnId]: filterValue } });
  };
  const handleSaveTableCustomization = (updates) => {
    updatePreferences(updates);
    toast({ title: 'Table customization saved', status: 'success', duration: 2000 });
  };
  const handleResetTableCustomization = async () => {
    await resetTablePrefs();
    toast({ title: 'Reset to defaults', status: 'success', duration: 2000 });
  };
  
  if (contactsLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDirection="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading CRM Dashboard...</Text>
        </Center>
      </Container>
    );
  }

  // --- REFACTORED JSX ---
  return (
    <Box bg={bgColor} minH="100vh" overflowX="hidden">
      {/* Sticky Header - zIndex removed to avoid blocking global nav popups (notifications/messages) */}
      <Box
        position="sticky"
        top={0}
        bg={headerBG}
        px={{ base: 4, md: 6, lg: 8 }}
        py={3}
        shadow="sm"
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <VStack align="stretch" spacing={3} maxW="100%" mx="auto">
          <HStack justify="space-between" align="center" data-tour="dashboard-header">
            <Heading size={{ base: 'md', md: 'lg' }}>
              {isViewingSelf
                ? '📊 CRM Dashboard'
                : `📊 ${crmUsers.find(u => u.uid === selectedUserId)?.name || 'User'}'s Dashboard`}
            </Heading>
          </HStack>
          <HStack spacing={2} flexWrap="wrap" justify={{ base: 'flex-start', sm: 'flex-end' }}>
            <Button
              size="sm"
              leftIcon={<FiMessageSquare />}
              onClick={onFeedbackOpen}
              variant="outline"
              display={{ base: 'none', md: 'flex' }}
            >
              Feedback/Ideas
            </Button>

            {isCRMAdmin && (
              <FormControl maxW={{ base: '100%', sm: '200px' }}>
                <ChakraSelect
                  size="sm"
                  placeholder="All Users"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  {(() => {
                    const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                    return (
                      <>
                        {dtcUsers.length > 0 && (
                          <optgroup label="DTC Loan Officers">
                            {dtcUsers.map((user) => (
                              <option key={user.uid} value={user.uid}>{user.name}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                          {otherUsers.map((user) => (
                            <option key={user.uid} value={user.uid}>{user.name}</option>
                          ))}
                        </optgroup>
                      </>
                    );
                  })()}
                </ChakraSelect>
              </FormControl>
            )}
            <Button
              size="sm"
              leftIcon={<FiSettings />}
              onClick={() => history.push('/crm/settings')}
              variant="outline"
              display={{ base: 'none', sm: 'flex' }}
            >
              Settings
            </Button>
            <IconButton
              size="sm"
              icon={<FiSettings />}
              onClick={() => history.push('/crm/settings')}
              variant="outline"
              display={{ base: 'flex', sm: 'none' }}
              aria-label="Settings"
            />
            
            <Button
              size="sm"
              leftIcon={<FiUpload />}
              onClick={onImportOpen}
              variant="outline"
              display={{ base: 'none', md: 'flex' }}
              data-tour="dashboard-import"
            >
              Import
            </Button>
            
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              onClick={onBulkExportOpen}
              variant="outline"
              display={{ base: 'none', md: 'flex' }}
              data-tour="dashboard-export"
            >
              Export
            </Button>
            
            <Button
              size="sm"
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onAddContactOpen}
              data-tour="dashboard-new-contact"
            >
              New Contact
            </Button>
            
            {/* More menu - includes Import/Export on mobile */}
            <Menu>
              <MenuButton as={IconButton} icon={<FiMoreVertical />} size="sm" variant="ghost" />
              <MenuList>
                {/* Mobile-only options */}
                <MenuItem icon={<FiUpload />} onClick={onImportOpen} display={{ base: 'flex', md: 'none' }}>
                  Import Contacts
                </MenuItem>
                <MenuItem icon={<FiDownload />} onClick={onBulkExportOpen} display={{ base: 'flex', md: 'none' }}>
                  Export Contacts
                </MenuItem>
                {isCRMAdmin && (
                  <MenuItem icon={<FiShield />} onClick={() => history.push('/crm/admin')}>
                    Admin Panel
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </HStack>
        </VStack>
      </Box>

      {/* Main Content Area */}
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="100%" mx="auto">
          
          {/* FutureBot Daily Briefing (admin preview) */}
          {canAccessNewFeatures({ currentUser, SuperAdmin, Roles }) && (
            <Box data-tour="dashboard-briefing">
              <DailyBriefingCard currentUser={currentUser} />
            </Box>
          )}

          {/* Data Integrity Warning - Removed (incompatible with pagination) */}
          
          {/* KPIs - Updated Layout with Anniversaries */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} data-tour="dashboard-kpis">
            {/* Active Deals Card */}
            <Box
              bg={widgetBG}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={5}
              shadow="sm"
              transition="all 0.2s"
              _hover={{ shadow: 'md' }}
            >
              <HStack justify="space-between" align="flex-start">
                <Stat>
                  <HStack spacing={2}>
                    <StatLabel fontSize="xs" color="gray.600">
                      Active Deals
                    </StatLabel>
                    <Tooltip
                      label={`All deals in an open pipeline stage${isCRMAdmin ? ' for the user selected above (or all users)' : ' that you own'}. Deals in Funded, Broker Check Received, Finalized, or Lost stages are excluded. Not affected by the contact filters or search below.`}
                      fontSize="sm"
                      hasArrow
                      placement="top"
                    >
                      <Box>
                        <Icon as={FiInfo} boxSize={3} color="gray.400" />
                      </Box>
                    </Tooltip>
                  </HStack>
                  <StatNumber fontSize="3xl" mt={2}>
                    {stats.activeDeals}
                  </StatNumber>
                  <StatHelpText mt={1} fontSize="xs">
                    in pipeline
                  </StatHelpText>
                </Stat>
                <Box
                  bg={activeDealsIconBg}
                  p={3}
                  borderRadius="lg"
                >
                  <Icon as={FiAlertCircle} boxSize={6} color="green.500" />
                </Box>
              </HStack>
            </Box>

            {/* Anniversaries & Birthdays Widget */}
            <LoanAnniversariesWidget
              userId={effectiveUserId}
              onContactClick={handleContactClick}
            />

            {/* My Tasks Widget */}
            <MyTasksWidget
              tasks={tasks}
              onTaskComplete={handleTaskComplete}
              onTaskClick={handleTaskClick}
              onViewAll={() => history.push('/tasks')}
            />
          </SimpleGrid>

          {/* Contacts Table Section */}
          <Box width="100%">
            {/* Contacts Table */}
              <Box bg={widgetBG} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={{ base: 4, md: 6 }} shadow="sm" data-tour="dashboard-table">
                <VStack align="stretch" spacing={4}>
                  {/* Table Header Row */}
                  <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <HStack spacing={2}>
                      <Text fontSize="lg" fontWeight="semibold">Contacts</Text>
                      <Badge colorScheme="blue" fontSize="md" px={2} py={1} borderRadius="md">
                        {contactsTotalCount.toLocaleString()}
                      </Badge>
                    </HStack>
                    <HStack spacing={2}>
                      {/* Search */}
                      <InputGroup size="sm" maxW="220px" data-tour="dashboard-search">
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiSearch} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input
                          placeholder="Search contacts..."
                          value={tableSearchTerm}
                          onChange={(e) => setTableSearchTerm(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                          pr={tableSearchTerm ? '40px' : undefined}
                        />
                        {tableSearchTerm && (
                          <InputRightElement>
                            <IconButton
                              icon={<FiX />}
                              size="xs"
                              variant="ghost"
                              aria-label="Clear search"
                              onClick={handleClearSearch}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <Tooltip label="Customize table columns">
                        <IconButton icon={<FiSettings />} aria-label="Customize table" size="sm" variant="outline" onClick={onTableCustomOpen} data-tour="dashboard-customize" />
                      </Tooltip>
                    </HStack>
                  </HStack>
                  
                  {/* Filter Toolbar */}
                  <Box data-tour="dashboard-filters">
                  <ContactsFilterToolbar
                    filters={activeFilters}
                    onFiltersChange={applyFilters}
                    smartLists={smartLists}
                    personalLists={personalLists}
                    globalLists={globalLists}
                    selectedSmartListId={selectedNewListId}
                    onSelectSmartList={(listId, listFilters) => {
                      setSelectedNewListId(listId);
                      if (listFilters) {
                        applyFilters(listFilters);
                      }
                    }}
                    onCreateSmartList={createSmartList}
                    onUpdateSmartList={updateSmartList}
                    onDeleteSmartList={deleteSmartList}
                    onDuplicateSmartList={duplicateSmartList}
                    onSetDefaultSmartList={setSmartListAsDefault}
                    loading={smartListsLoading}
                  />
                  </Box>
                  
                  {/* Contacts Table - Server-side pagination, sorting, filtering */}
                  <CustomizableContactsTable
                      contacts={contacts}
                      totalCount={contactsTotalCount}
                      currentPage={contactsCurrentPage}
                      totalPages={contactsTotalPages}
                      pageSize={contactsPageSize}
                      onPageChange={setContactsPage}
                      onPageSizeChange={setContactsPageSize}
                      sortBy={contactsSort?.field || tablePreferences.sortBy}
                      sortDirection={contactsSort?.direction || tablePreferences.sortDirection}
                      onSortChange={(field, direction) => {
                        applySort(field, direction);
                        handleSortChange(field, direction);
                      }}
                      loading={contactsLoading}
                      visibleColumns={tablePreferences.visibleColumns}
                      columnOrder={tablePreferences.columnOrder}
                      selectedContacts={selectedContactIds}
                      onSelectionChange={handleSelectionChange}
                      onSelectAllMatchingFilters={handleSelectAllMatchingFilters}
                      isAllMatchingFiltersSelected={isAllContactsSelected}
                      isSelectAllLoading={isSelectAllLoading}
                      onClearAllSelection={handleClearAllSelection}
                      onContactClick={handleContactClick}
                      onCopyEmail={(email) => {
                        navigator.clipboard.writeText(email);
                        toast({ title: 'Email copied', status: 'success', duration: 2000 });
                      }}
                      onCopyPhone={(phone) => {
                        navigator.clipboard.writeText(phone);
                        toast({ title: 'Phone copied', status: 'success', duration: 2000 });
                      }}
                      usersMap={usersMap}
                      ownerChipViewerId={isCRMAdmin ? null : currentUser?.uid}
                    />
                  {/* "Load More" button removed - using server-side pagination */}
                </VStack>
              </Box>
          </Box>
        </VStack>

        {/* Modals and Drawers */}
        {selectedContact && (
          <ContactDetailDrawer
            isOpen={isContactDetailOpen}
            onClose={() => {
              onContactDetailClose();
              setSelectedContact(null);
              if (urlContactId) history.push('/crm/dashboard');
            }}
            contact={selectedContact}
            contactId={selectedContact.id}
            onContactUpdated={(updatedContact) => {
              setSelectedContact(updatedContact);
              refreshContacts();
            }}
            onContactDeleted={() => {
              refreshContacts();
            }}
            crmUsers={crmUsers}
          />
        )}
        <QuickAddContact isOpen={isAddContactOpen} onClose={onAddContactClose} onContactCreated={handleContactCreated} />
        <SmartListBuilderModal
          isOpen={isSmartListBuilderOpen}
          onClose={onSmartListBuilderClose}
          onSave={async (listData, listId) => {
            try {
              if (listId) {
                await updateSmartList(listId, listData);
                toast({ title: 'Smart List updated', status: 'success', duration: 3000 });
              } else {
                await createSmartList(listData);
                toast({ title: 'Smart List created', status: 'success', duration: 3000 });
              }
            } catch (error) {
              toast({ title: 'Error saving Smart List', description: error.message, status: 'error', duration: 3000 });
            }
          }}
        />
        <TaskDetailModal
          isOpen={isTaskDetailOpen}
          onClose={onTaskDetailClose}
          task={selectedTask}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
          onComplete={handleTaskCompleteFromDetail}
          onReopen={reopenTask}
        />
        {/* Task edit modal - same Supabase-native editor the /tasks page uses.
            The previous TaskModal was never given the task being edited, so
            "editing" from the dashboard silently created a duplicate task. */}
        <UnifiedEventModal
          isOpen={isTaskModalOpen}
          onClose={handleTaskEditModalClose}
          onSave={handleTaskEditModalClose}
          initialEventType="task"
          editingTask={selectedTask}
          crmUsers={crmUsers}
        />
        {/* Post-completion "log an activity?" prompt */}
        <TaskCompletionActivityPrompt
          isOpen={isActivityPromptOpen}
          onClose={handleActivityPromptClose}
          task={completedTaskForPrompt}
          onCreateTask={(taskData) => handleSaveTask(null, taskData)}
        />
        <TableCustomizationModal
          isOpen={isTableCustomOpen}
          onClose={onTableCustomClose}
          visibleColumns={tablePreferences.visibleColumns}
          columnOrder={tablePreferences.columnOrder}
          onSave={handleSaveTableCustomization}
          onReset={handleResetTableCustomization}
          allColumns={ALL_CONTACTS_COLUMNS}
        />
        <ImportModal
          isOpen={isImportOpen}
          onClose={onImportClose}
          importType={isCRMAdmin ? 'contacts-with-assignment' : 'contacts'}
          autoAssignTo={currentUser?.uid}
          allowAssignmentSelection={isCRMAdmin}
          crmUsers={isCRMAdmin ? crmUsers : []}
          onImportComplete={() => toast({ title: 'Import complete', status: 'success', duration: 3000 })}
        />
        {selectedContactIds.length > 0 && (
          <BulkContactActionsBar
            count={selectedContactIds.length}
            onUpdateTags={onBulkTagOpen}
            onUpdateTypes={onBulkTypeOpen}
            onTransfer={onBulkOwnershipOpen}
            onDelete={onBulkDeleteOpen}
            onExport={onBulkExportOpen}
            onSendEmail={onBulkEmailOpen}
            onSendSMS={onBulkSMSOpen}
            onSendPostcard={canSendPostcards ? onBulkPostcardOpen : null}
            onEnrollCampaign={onBulkEnrollOpen}
            onClear={handleClearAllSelection}
            isCRMAdmin={isCRMAdmin}
            isAllSelected={isAllContactsSelected}
            dialpadEnabled={dialpadEnabled}
            onSetRecordScope={isCRMAdmin ? onBulkScopeOpen : null}
          />
        )}
        <BulkContactTagModal isOpen={isBulkTagOpen} onClose={onBulkTagClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkContactTypeModal isOpen={isBulkTypeOpen} onClose={onBulkTypeClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkContactOwnershipModal isOpen={isBulkOwnershipOpen} onClose={onBulkOwnershipClose} selectedContacts={selectedContactIds} crmUsers={crmUsers} onSuccess={handleBulkSuccess} />
        <BulkContactRecordScopeModal isOpen={isBulkScopeOpen} onClose={onBulkScopeClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkDeleteContactsModal isOpen={isBulkDeleteOpen} onClose={onBulkDeleteClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkExportContactsModal isOpen={isBulkExportOpen} onClose={onBulkExportClose} selectedContacts={selectedContactIds} fetchContactsByIds={fetchContactsByIds} />
        <BulkEmailModal isOpen={isBulkEmailOpen} onClose={onBulkEmailClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkSMSModal isOpen={isBulkSMSOpen} onClose={onBulkSMSClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkPostcardModal isOpen={isBulkPostcardOpen} onClose={onBulkPostcardClose} recipientType="contacts" ids={selectedContactIds} onSuccess={handleBulkSuccess} />
        <BulkEnrollCampaignModal isOpen={isBulkEnrollOpen} onClose={onBulkEnrollClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
        <CRMAddIdeaModal isOpen={isFeedbackOpen} onClose={onFeedbackClose} />
      </Container>
    </Box>
  );
}

// CrmSettingsProvider is at the app root (App.jsx) — no need to wrap again here
export default function DashboardPage() {
  const location = useLocation();
  const { contactId } = useParams();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Resolve the admin's default scope BEFORE mounting the page, so the contacts
  // query and realtime channel open once with the right owner instead of firing
  // for the wrong owner and immediately re-firing.
  const { defaultOwnerScope, loading: viewPrefLoading } = useCrmDefaultOwnerScope(
    isCRMAdmin ? currentUser?.uid : null
  );

  // Restore this tab's last view, but only on a bare list URL. A query string
  // means a deep link or shared URL, which must win. A :contactId means the
  // drawer needs that contact in the result set, so replaying filters could
  // hide it and the drawer would never open.
  const savedSearch = useMemo(
    () => (location.search || contactId ? null : readViewSearch(CRM_VIEW_KEYS.dashboard)),
    [location.search, contactId]
  );

  // Fixing the URL before the content mounts lets its existing parse-on-mount
  // logic do all the rehydration, and keeps the contacts query to a single fetch.
  if (savedSearch) {
    return <Redirect to={{ pathname: location.pathname, search: savedSearch }} />;
  }

  if (viewPrefLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDirection="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading CRM Dashboard...</Text>
        </Center>
      </Container>
    );
  }

  return <DashboardPageContent defaultOwnerScope={defaultOwnerScope} />;
}
